import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Activity, CheckCircle2, Copy, Eye, EyeOff, KeyRound, Loader2, Network, Play, RefreshCw, Square,
  Trash2, Wand2, XCircle,
} from 'lucide-react';
import { api } from '../api/client.js';
import { Button } from '../components/Button.jsx';
import { Card } from '../components/Card.jsx';
import { Banner } from '../components/Banner.jsx';
import { Input } from '../components/Field.jsx';

/**
 * Servers → Proxy (2026-10-01): SOCKS5 + HTTP proxy exiting on this server's
 * IP, installed without touching anything already on the server.
 *
 * ⚠️ The password is typed here, sent once to the backend (which hands it to
 * the server over SSH stdin and drops it), and NEVER stored by the panel. The
 * connection strings are built in this component from what was typed and shown
 * once; "Done" wipes them from memory. A later Test or Reset needs it typed
 * again — by design.
 */

const PASS_RE = /^[A-Za-z0-9._~+=-]{12,128}$/;
const USER_RE = /^[A-Za-z0-9._-]{3,32}$/;
const PASS_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

function generatePassword(len = 24) {
  const bytes = new Uint32Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => PASS_CHARS[b % PASS_CHARS.length]).join('');
}

function randomPort() {
  const b = new Uint32Array(1);
  crypto.getRandomValues(b);
  return 20000 + (b[0] % 40000); // 20000-59999, so +1 stays in range
}

function Check({ ok, warn, label, detail }) {
  const Icon = ok ? CheckCircle2 : warn ? Activity : XCircle;
  const tone = ok ? 'text-ok' : warn ? 'text-warn' : 'text-err';
  return (
    <div className="flex items-start gap-2 text-[13px]">
      <Icon className={`mt-0.5 w-4 h-4 shrink-0 ${tone}`} />
      <div className="min-w-0">
        <span className="text-t1">{label}</span>
        {detail && <span className="text-t3"> — {detail}</span>}
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, hint }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Input
        label={label}
        mono
        className="w-72"
        type={show ? 'text' : 'password'}
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        hint={hint}
      />
      <Button size="sm" icon={show ? EyeOff : Eye} className="mb-[22px]" onClick={() => setShow((v) => !v)}>
        {show ? 'Hide' : 'Show'}
      </Button>
      <Button size="sm" icon={Wand2} className="mb-[22px]" onClick={() => { onChange(generatePassword()); setShow(true); }}>
        Generate
      </Button>
    </div>
  );
}

function CopyLine({ text }) {
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 break-all rounded-ctl border border-bd bg-s-1 px-2 py-1 font-mono text-[12px] text-t1">{text}</code>
      <Button size="sm" icon={Copy} onClick={() => navigator.clipboard?.writeText(text)}>Copy</Button>
    </div>
  );
}

function TestResult({ test }) {
  if (!test) return null;
  const ipDetail = (p) => (p.outcome === 'ok'
    ? `exit IP ${p.exit_ip}${p.exit_ip_matches ? ' = server ✓' : ` ≠ server ${test.server_ip ?? '?'}`}`
    : `${p.outcome}: ${p.detail ?? ''}`);
  const refusedDetail = (p) => (p.refused ? 'refused ✓' : `NOT refused (${p.outcome}${p.detail ? `: ${p.detail}` : ''})`);
  return (
    <div className="space-y-1.5">
      {test.open_proxy && (
        <Banner kind="error" compact title="OPEN PROXY">
          A request without valid credentials was served. Remove the proxy now — an open proxy gets this IP blacklisted within hours.
        </Banner>
      )}
      <Check ok={test.socks5.exit_ip_matches} label="SOCKS5 with the password" detail={ipDetail(test.socks5)} />
      <Check ok={test.http.exit_ip_matches} label="HTTP with the password" detail={ipDetail(test.http)} />
      <Check ok={test.socks5_wrong_password.refused} label="SOCKS5, wrong password" detail={refusedDetail(test.socks5_wrong_password)} />
      <Check ok={test.http_wrong_password.refused} label="HTTP, wrong password" detail={refusedDetail(test.http_wrong_password)} />
      <Check ok={test.socks5_no_auth.refused} label="SOCKS5, no credentials" detail={refusedDetail(test.socks5_no_auth)} />
      <Check ok={test.http_no_auth.refused} label="HTTP, no credentials" detail={refusedDetail(test.http_no_auth)} />
      <p className="text-[12px] text-t3">tested {new Date(test.tested_at).toLocaleString()} from the panel</p>
    </div>
  );
}

function DiffView({ title, diff, expectIdentical }) {
  if (!diff) return null;
  const good = expectIdentical ? diff.identical : diff.foreign.length === 0;
  const summary = expectIdentical
    ? (diff.identical ? 'identical to "before" ✓' : `${diff.changed_lines} line(s) differ from "before"`)
    : (diff.foreign.length === 0
      ? `${diff.changed_lines} changed line(s), all the proxy's own ✓`
      : `${diff.foreign.length} changed line(s) are NOT the proxy's`);
  return (
    <details className="rounded-ctl border border-bd bg-s-1 p-3">
      <summary className="cursor-pointer text-[13px]">
        <span className="font-medium text-t1">{title}</span>{' '}
        <span className={good ? 'text-ok' : 'text-err'}>{summary}</span>
      </summary>
      {!expectIdentical && diff.foreign.length > 0 && (
        <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-err">{diff.foreign.join('\n')}</pre>
      )}
      {Object.entries(diff.sections).map(([name, s]) => (
        <div key={name} className="mt-2">
          <div className="font-mono text-[11.5px] font-semibold text-t2">## {name}</div>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11px]">
            {s.removed.map((l) => `- ${l}`).concat(s.added.map((l) => `+ ${l}`)).join('\n')}
          </pre>
        </div>
      ))}
    </details>
  );
}

export function ServerProxyPanel({ server }) {
  const [info, setInfo] = useState(null);
  const [live, setLive] = useState(null);
  const [liveErr, setLiveErr] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  // install form
  const [socksPort, setSocksPort] = useState(() => randomPort());
  const [httpPort, setHttpPort] = useState(null);
  const [username, setUsername] = useState('proxy');
  const [password, setPassword] = useState('');
  const [preflight, setPreflight] = useState(null);
  const [pfErr, setPfErr] = useState(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfFor, setPfFor] = useState(null);

  // jobs
  const [jobLog, setJobLog] = useState('');
  const [jobRunning, setJobRunning] = useState(false);
  const [jobOutcome, setJobOutcome] = useState(null);
  const [reveal, setReveal] = useState(null); // { user, pass, socks, http } — shown once
  const pollRef = useRef(null);

  // installed actions
  const [testPw, setTestPw] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [newPw, setNewPw] = useState('');

  const effHttpPort = httpPort ?? socksPort + 1;

  const load = useCallback(async () => {
    try {
      setInfo(await api(`/api/servers/${server.id}/proxy`));
    } catch (err) {
      setNotice({ kind: 'error', text: err.body?.detail ?? err.message });
    }
  }, [server.id]);

  const loadLive = useCallback(async () => {
    setLiveLoading(true);
    setLiveErr(null);
    try {
      setLive((await api(`/api/servers/${server.id}/proxy/live`)).live);
    } catch (err) {
      setLiveErr(err.body?.detail ?? err.message);
    } finally {
      setLiveLoading(false);
    }
  }, [server.id]);

  useEffect(() => { load(); loadLive(); }, [load, loadLive]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const rec = info?.proxy;
  const installed = live ? live.installed : rec?.status === 'installed';

  function pollJob(jobId, onDone) {
    setJobRunning(true);
    setJobLog('');
    setJobOutcome(null);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const p = await api(`/api/servers/${server.id}/proxy/jobs/${jobId}`);
        setJobLog(p.log ?? '');
        if (p.job.status === 'done' || p.job.status === 'failed') {
          clearInterval(pollRef.current);
          setJobRunning(false);
          setJobOutcome(p.job);
          onDone?.(p.job);
          load();
          loadLive();
        }
      } catch { /* keep polling */ }
    }, 2000);
  }

  async function runPreflight() {
    setPfLoading(true);
    setPfErr(null);
    setPreflight(null);
    try {
      const r = await api(`/api/servers/${server.id}/proxy/preflight`, {
        method: 'POST', body: { socks_port: socksPort, http_port: effHttpPort },
      });
      setPreflight(r.preflight);
      setPfFor(`${socksPort}/${effHttpPort}`);
    } catch (err) {
      setPfErr(err.body?.detail ?? err.message);
    } finally {
      setPfLoading(false);
    }
  }

  async function install() {
    if (!USER_RE.test(username)) return window.alert('Username: 3-32 chars, letters, digits and . _ -');
    if (!PASS_RE.test(password)) return window.alert('Password: 12-128 chars, letters, digits and . _ ~ + = -');
    if (!window.confirm(
      `Install a SOCKS5 (port ${socksPort}) + HTTP (port ${effHttpPort}) proxy on "${server.name}"?\n\n`
      + 'Adds only its own binary, config, systemd unit and (if a firewall is active) one allow rule per port. Nothing else on the server is changed.',
    )) return;
    const typed = { user: username, pass: password, socks: socksPort, http: effHttpPort };
    try {
      const r = await api(`/api/servers/${server.id}/proxy/install`, {
        method: 'POST',
        body: { socks_port: socksPort, http_port: effHttpPort, username, password },
      });
      setPassword('');
      pollJob(r.job_id, (job) => {
        if (job.status === 'done') setReveal(typed);
        setTestResult(job.result?.test ?? null);
      });
    } catch (err) {
      const d = err.body?.detail;
      window.alert(`Install refused: ${Array.isArray(d) ? d.join('\n') : d ?? err.message}`);
    }
  }

  async function runTest() {
    if (!PASS_RE.test(testPw)) return window.alert('Type the proxy password (the panel does not keep it).');
    setBusy(true);
    try {
      const r = await api(`/api/servers/${server.id}/proxy/test`, { method: 'POST', body: { password: testPw } });
      setTestResult(r.test);
      setTestPw('');
      load();
    } catch (err) {
      setNotice({ kind: 'error', text: `Test failed: ${err.body?.detail ?? err.message}` });
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!PASS_RE.test(newPw)) return window.alert('New password: 12-128 chars, letters, digits and . _ ~ + = -');
    if (!window.confirm('Replace the proxy password? Clients using the old one stop working.')) return;
    setBusy(true);
    const typed = { user: rec?.username ?? live?.username, pass: newPw, socks: rec?.socks_port ?? live?.socks_port, http: rec?.http_port ?? live?.http_port };
    try {
      await api(`/api/servers/${server.id}/proxy/reset-password`, { method: 'POST', body: { password: newPw } });
      setNewPw('');
      setReveal(typed);
      setNotice({ kind: 'success', text: 'Password changed. Copy the new connection strings below — they are shown once.' });
      loadLive();
    } catch (err) {
      setNotice({ kind: 'error', text: `Reset failed: ${err.body?.detail ?? err.message}` });
    } finally {
      setBusy(false);
    }
  }

  async function startStop(action) {
    setBusy(true);
    try {
      const r = await api(`/api/servers/${server.id}/proxy/${action}`, { method: 'POST' });
      setNotice({ kind: 'success', text: `Proxy ${action === 'start' ? 'started' : 'stopped'} (${r.active}).` });
      loadLive();
    } catch (err) {
      setNotice({ kind: 'error', text: `${action} failed: ${err.body?.detail ?? err.message}` });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const typed = window.prompt(`Remove the proxy and put "${server.name}" back exactly as before?\nType the server name to confirm:`);
    if (typed !== server.name) return;
    setReveal(null);
    try {
      const r = await api(`/api/servers/${server.id}/proxy/remove`, { method: 'POST', body: { confirm_name: typed } });
      pollJob(r.job_id);
    } catch (err) {
      window.alert(`Remove refused: ${err.body?.detail ?? err.message}`);
    }
  }

  const host = server.host;
  const pfMatches = preflight && pfFor === `${socksPort}/${effHttpPort}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-t1">
        <Network className="w-4 h-4 text-ic" />
        Proxy — {server.name}
        {rec && <span className="rounded-xl bg-s-3 px-[9px] py-1 text-[11.5px] font-medium text-t2">{rec.status}</span>}
        <span className="text-[12px] font-normal text-t3">gost {info?.gost_version} · SOCKS5 + HTTP · auth required</span>
      </div>

      {notice && <Banner compact kind={notice.kind} title={notice.kind === 'error' ? 'Error' : 'Done'}>{notice.text}</Banner>}

      {/* Live status */}
      <Card className="space-y-2 p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13.5px] font-medium text-t1">Status on the server</span>
          <Button size="sm" icon={liveLoading ? Loader2 : RefreshCw} className={liveLoading ? '[&>svg]:animate-spin' : ''} onClick={loadLive} disabled={liveLoading}>
            Refresh
          </Button>
        </div>
        {liveErr && <Banner compact kind="error" title="Could not read the server">{liveErr}</Banner>}
        {live && !live.installed && <p className="text-[13px] text-t2">No proxy installed.</p>}
        {live?.installed && (
          <div className="grid gap-1 text-[13px] sm:grid-cols-2">
            <Check ok={live.active === 'active'} warn={live.active !== 'active'} label={`Service: ${live.active}`} detail={`enabled: ${live.enabled}`} />
            <Check ok label={`User: ${live.username}`} detail={`gost ${live.gost_version}`} />
            <Check ok={live.socks_listening} warn={!live.socks_listening} label={`SOCKS5 ${host}:${live.socks_port}`} detail={live.socks_listening ? 'listening' : 'not listening'} />
            <Check ok={live.http_listening} warn={!live.http_listening} label={`HTTP ${host}:${live.http_port}`} detail={live.http_listening ? 'listening' : 'not listening'} />
            <Check ok label={`Firewall: ${live.firewall}`} detail={live.fw_added_ports ? `rules we added: ${live.fw_added_ports}` : 'no rule added'} />
          </div>
        )}
      </Card>

      {/* Shown once */}
      {reveal && (
        <Card className="space-y-2 border-warn-bd p-4">
          <div className="text-[13.5px] font-medium text-t1">Connection strings — shown once, the panel does not keep the password</div>
          <CopyLine text={`socks5://${reveal.user}:${reveal.pass}@${host}:${reveal.socks}`} />
          <CopyLine text={`http://${reveal.user}:${reveal.pass}@${host}:${reveal.http}`} />
          <CopyLine text={`curl -x socks5h://${reveal.user}:${reveal.pass}@${host}:${reveal.socks} https://api.ipify.org`} />
          <Button size="sm" onClick={() => setReveal(null)}>Done — I copied it, hide</Button>
        </Card>
      )}

      {/* Job log */}
      {(jobRunning || jobLog) && (
        <Card className="space-y-2 p-4">
          <div className="text-[13.5px] font-medium text-t1">
            {jobRunning ? 'Running…' : jobOutcome?.status === 'done' ? '✓ Finished' : '✗ Failed'}
            {jobOutcome?.status === 'failed' && jobOutcome.error?.detail && (
              <span className="ml-2 font-normal text-err">{jobOutcome.error.error}: {jobOutcome.error.detail}</span>
            )}
          </div>
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-ctl bg-t1 p-3 font-mono text-[11px] text-s-2">{jobLog || 'starting…'}</pre>
        </Card>
      )}

      {/* Install */}
      {live && !live.installed && !jobRunning && (
        <Card className="space-y-3 p-4">
          <span className="text-[13.5px] font-medium text-t1">Install a proxy</span>
          {info?.has_mail && (
            <Banner kind="error" compact title="This server also sends mail">
              A leaked or weak proxy password lets anyone send traffic from this IP, and that burns the mail reputation (blacklists).
              Use a long generated password, and remove the proxy when you no longer need it.
            </Banner>
          )}
          <div className="flex flex-wrap items-start gap-2">
            <Input label="SOCKS5 port" mono className="w-32" type="number" value={socksPort}
              onChange={(e) => { setSocksPort(Number(e.target.value)); setPreflight(null); }} />
            <Input label="HTTP port" mono className="w-32" type="number" value={effHttpPort}
              onChange={(e) => { setHttpPort(Number(e.target.value)); setPreflight(null); }} />
            <Input label="Username" mono className="w-40" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <PasswordField label="Password (never stored by the panel)" value={password} onChange={setPassword}
            hint="12-128 chars: letters, digits, . _ ~ + = -" />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" icon={pfLoading ? Loader2 : RefreshCw} className={pfLoading ? '[&>svg]:animate-spin' : ''} onClick={runPreflight} disabled={pfLoading}>
              Preflight
            </Button>
            <Button variant="primary" icon={Network} onClick={install}
              disabled={!pfMatches || !preflight.can_install || !PASS_RE.test(password) || !USER_RE.test(username)}
              title={!pfMatches ? 'Run preflight for these ports first' : undefined}>
              Install
            </Button>
          </div>
          {pfErr && <Banner compact kind="error" title="Preflight failed">{pfErr}</Banner>}
          {preflight && (
            <div className="space-y-1.5">
              <Check ok={preflight.os.supported} label={`OS ${preflight.os.id} ${preflight.os.version}`} />
              <Check ok={preflight.systemd.ok} label={`systemd ${preflight.systemd.version}`} detail="needs >= 247" />
              <Check ok={preflight.arch_ok} label={`CPU ${preflight.arch}`} />
              <Check ok={preflight.downloader !== 'none'} label={`downloader: ${preflight.downloader}`} />
              <Check ok={preflight.socks_port_free === true} label={`port ${socksPort} free`} />
              <Check ok={preflight.http_port_free === true} label={`port ${effHttpPort} free`} />
              <Check ok warn={preflight.firewall === 'custom'} label={`firewall: ${preflight.firewall}`}
                detail={{
                  ufw: 'active — one allow rule per port will be added, and removed on Remove',
                  firewalld: 'active — one port per proxy port will be added, and removed on Remove',
                  none: 'none active — nothing to open (a firewall that is off stays off)',
                  custom: 'hand-made ruleset — NOT touched; open the ports yourself if needed',
                }[preflight.firewall]} />
              {preflight.blockers.length > 0 && (
                <Banner compact kind="error" title="Cannot install">{preflight.blockers.join(' · ')}</Banner>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Installed: operate */}
      {live?.installed && !jobRunning && (
        <Card className="space-y-4 p-4">
          <div className="flex flex-wrap gap-2">
            {live.active === 'active'
              ? <Button size="sm" icon={Square} disabled={busy} onClick={() => startStop('stop')}>Stop</Button>
              : <Button size="sm" icon={Play} disabled={busy} onClick={() => startStop('start')}>Start</Button>}
            <Button size="sm" variant="danger" icon={Trash2} disabled={busy} onClick={remove}>Remove</Button>
          </div>

          <div className="space-y-2">
            <span className="text-[13px] font-medium text-t1">Test from the panel</span>
            <div className="flex flex-wrap items-end gap-2">
              <Input label="Proxy password" mono className="w-72" type="password" autoComplete="off" value={testPw} onChange={(e) => setTestPw(e.target.value)} />
              <Button size="sm" icon={busy ? Loader2 : Activity} className="mb-[2px]" disabled={busy} onClick={runTest}>Test</Button>
            </div>
          </div>

          <div className="space-y-2">
            <span className="text-[13px] font-medium text-t1">Reset password</span>
            <PasswordField label="New password" value={newPw} onChange={setNewPw} />
            <Button size="sm" icon={KeyRound} disabled={busy || !PASS_RE.test(newPw)} onClick={resetPassword}>Set new password</Button>
          </div>
        </Card>
      )}

      {(testResult || rec?.last_test) && (
        <Card className="space-y-2 p-4">
          <span className="text-[13.5px] font-medium text-t1">Last test</span>
          <TestResult test={testResult ?? rec.last_test} />
        </Card>
      )}

      {(rec?.install_diff || rec?.remove_diff) && (
        <div className="space-y-2">
          <DiffView title="Install: before → after" diff={rec.install_diff} expectIdentical={false} />
          <DiffView title="Remove: before → after removal" diff={rec.remove_diff} expectIdentical />
        </div>
      )}
    </div>
  );
}
